/**
 * Donations Domain Contract — Zod Schemas
 *
 * Shared contract definitions for the donations bounded context.
 * Covers Parnes HaYom, general campaigns, annual fund, and
 * school-admin moderation/ledger/reconciliation.
 *
 * Authority: apps/ystnj/documentation/prd/parent-portal/parnes-hayom/v1.1/prd.md
 * Pattern: packages/domain-core/src/contracts/payments-contract.ts
 */

import { z } from "zod";
import { OrgId, UserId, UnixTs } from "../types/branded";
import type { CampaignCalendarGrid } from "../services/halachic-calendar/calendarTypes";
import { DonationPaymentRecordSchema } from "./payments-contract";

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const DonationCampaignTypeSchema = z.enum([
  "general",
  "annual-fund",
  "parnes-hayom",
]);
export type DonationCampaignType = z.infer<typeof DonationCampaignTypeSchema>;

export const DonationStatusSchema = z.enum([
  "PENDING",
  "RECOMMENDATION_SUBMITTED",
  "PAID",
  "FAILED",
  "RECOMMENDATION_VOIDED",
  "REFUND_INITIATED",
  "REFUNDED",
  "CONFLICT_DETECTED",
  "CONFLICT_REFUND_INITIATED",
  "CONFLICT_REFUNDED",
  "OFFLINE_RECEIVED",
  "OFFLINE_DEPOSITED",
]);
export type DonationStatus = z.infer<typeof DonationStatusSchema>;

export const DonationMethodSchema = z.enum([
  "stripe",
  "sola_daf",
  "offline_check",
  "offline_cash",
  "offline_other",
]);
export type DonationMethod = z.infer<typeof DonationMethodSchema>;

export const DedicationTypeSchema = z.enum(["HONOR", "MEMORY"]);
export type DedicationTypeEnum = z.infer<typeof DedicationTypeSchema>;

export const ModerationStatusSchema = z.enum([
  "PENDING",
  "APPROVED",
  "REJECTED",
]);
export type ModerationStatus = z.infer<typeof ModerationStatusSchema>;

export const ParnesHayomSponsorshipTierSchema = z.enum([
  "one_class",
  "one_division",
  "schoolwide",
]);
export type ParnesHayomSponsorshipTier = z.infer<
  typeof ParnesHayomSponsorshipTierSchema
>;

// ---------------------------------------------------------------------------
// Campaign
// ---------------------------------------------------------------------------

export const DonationCampaignSchema = z
  .object({
    id: z.string().uuid(),
    tenantId: OrgId,
    name: z.string().min(1),
    campaignType: DonationCampaignTypeSchema,
    startDate: z.string().min(10), // ISO date
    endDate: z.string().min(10),
    priceCents: z.number().int().nonnegative(),
    blackoutDates: z.array(z.string().min(10)).optional(),
    slug: z.string().min(1),
    receiptOverrides: z.record(z.string(), z.string()).optional(),
    isActive: z.boolean(),
    createdAt: UnixTs,
    updatedAt: UnixTs,
  })
  .strict();
export type DonationCampaign = z.infer<typeof DonationCampaignSchema>;

// ---------------------------------------------------------------------------
// Donation
// ---------------------------------------------------------------------------

export const DonationSchema = z
  .object({
    id: z.string().uuid(),
    tenantId: OrgId,
    campaignId: z.string().uuid(),
    donorUserId: UserId.nullable().optional(),
    status: DonationStatusSchema,
    method: DonationMethodSchema,
    targetGregorianDate: z.string().min(10),
    sunsetApplied: z.boolean(),
    hebrewYear: z.number().int().positive(),
    hebrewMonth: z.number().int().positive(),
    hebrewDay: z.number().int().positive(),
    grossCents: z.number().int().nonnegative(),
    feeRecoveryCents: z.number().int().nonnegative(),
    netCents: z.number().int().nonnegative().optional(),
    sponsorshipTier: ParnesHayomSponsorshipTierSchema.nullable().optional(),
    sponsorshipDivisionId: z.string().nullable().optional(),
    sponsorshipDivisionLabel: z.string().nullable().optional(),
    dedicationType: DedicationTypeSchema.nullable().optional(),
    honoreeName: z.string().nullable().optional(),
    dedicationMessage: z.string().nullable().optional(),
    moderationStatus: ModerationStatusSchema,
    moderatedBy: UserId.nullable().optional(),
    moderatedAt: UnixTs.nullable().optional(),
    isAnonymous: z.boolean(),
    publicDisplayName: z.string().nullable().optional(),
    donorFirstName: z.string().min(1),
    donorLastName: z.string().min(1),
    donorEmail: z.string().email(),
    donorPhone: z.string().nullable().optional(),
    payment: DonationPaymentRecordSchema.nullable().optional(),
    stripeSessionId: z.string().nullable().optional(),
    stripePaymentIntentId: z.string().nullable().optional(),
    checkNumber: z.string().nullable().optional(),
    createdAt: UnixTs,
    updatedAt: UnixTs,
  })
  .strict();
export type Donation = z.infer<typeof DonationSchema>;

// ---------------------------------------------------------------------------
// Projections
// ---------------------------------------------------------------------------

/** Parent history view — subset of Donation for family donation listing. */
export const FamilyDonationHistoryItemSchema = z
  .object({
    id: z.string().uuid(),
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1),
    campaignType: DonationCampaignTypeSchema,
    targetGregorianDate: z.string().min(10),
    hebrewYear: z.number().int().positive(),
    hebrewMonth: z.number().int().positive(),
    hebrewDay: z.number().int().positive(),
    hebrewDateLabel: z.string(),
    grossCents: z.number().int().nonnegative(),
    feeRecoveryCents: z.number().int().nonnegative(),
    sponsorshipTier: ParnesHayomSponsorshipTierSchema.nullable().optional(),
    sponsorshipDivisionId: z.string().nullable().optional(),
    sponsorshipDivisionLabel: z.string().nullable().optional(),
    status: DonationStatusSchema,
    dedicationType: DedicationTypeSchema.nullable().optional(),
    honoreeName: z.string().nullable().optional(),
    moderationStatus: ModerationStatusSchema,
    createdAt: UnixTs,
  })
  .strict();
export type FamilyDonationHistoryItem = z.infer<
  typeof FamilyDonationHistoryItemSchema
>;

/** Teacher view — approved dedications only, donor identity masked. */
export const DailyHonoreeSchema = z
  .object({
    targetGregorianDate: z.string().min(10),
    hebrewDateLabel: z.string(),
    dedicationType: DedicationTypeSchema,
    honoreeName: z.string(),
    publicDisplayName: z.string().nullable().optional(),
    isAnonymous: z.boolean(),
  })
  .strict();
export type DailyHonoree = z.infer<typeof DailyHonoreeSchema>;

/** Admin ledger view — full donation record for reconciliation. */
export const LedgerEntrySchema = z
  .object({
    id: z.string().uuid(),
    campaignId: z.string().uuid(),
    campaignName: z.string(),
    campaignType: DonationCampaignTypeSchema,
    targetGregorianDate: z.string().min(10),
    hebrewDateLabel: z.string(),
    donorFirstName: z.string(),
    donorLastName: z.string(),
    donorEmail: z.string().email(),
    method: DonationMethodSchema,
    status: DonationStatusSchema,
    grossCents: z.number().int().nonnegative(),
    feeRecoveryCents: z.number().int().nonnegative(),
    netCents: z.number().int().nonnegative().optional(),
    sponsorshipTier: ParnesHayomSponsorshipTierSchema.nullable().optional(),
    sponsorshipDivisionId: z.string().nullable().optional(),
    sponsorshipDivisionLabel: z.string().nullable().optional(),
    dedicationType: DedicationTypeSchema.nullable().optional(),
    honoreeName: z.string().nullable().optional(),
    moderationStatus: ModerationStatusSchema,
    stripePaymentIntentId: z.string().nullable().optional(),
    checkNumber: z.string().nullable().optional(),
    createdAt: UnixTs,
    updatedAt: UnixTs,
  })
  .strict();
export type LedgerEntry = z.infer<typeof LedgerEntrySchema>;

// ---------------------------------------------------------------------------
// Campaign Calendar Response
// ---------------------------------------------------------------------------

/** Calendar response wrapping the CampaignCalendarGrid from the calendar service. */
export const CampaignCalendarResponseSchema = z
  .object({
    campaignId: z.string().uuid(),
    campaignName: z.string(),
    campaignType: DonationCampaignTypeSchema,
    priceCents: z.number().int().nonnegative(),
    startDate: z.string().min(10),
    endDate: z.string().min(10),
    // The grid itself is a runtime-only type; backend returns serialized JSON
    // that the frontend parses. Use z.custom for forward compatibility.
    grid: z.custom<CampaignCalendarGrid>(),
  })
  .strict();
export type CampaignCalendarResponse = z.infer<
  typeof CampaignCalendarResponseSchema
>;

// ---------------------------------------------------------------------------
// Tenant Config
// ---------------------------------------------------------------------------

export const FeeRecoveryRulesetSchema = z
  .object({
    defaultPercent: z.number().min(0).max(100),
    capCents: z.number().int().nonnegative().nullable().optional(),
    enabled: z.boolean(),
  })
  .strict();
export type FeeRecoveryRuleset = z.infer<typeof FeeRecoveryRulesetSchema>;

export const TenantParnasHayomConfigSchema = z
  .object({
    tenantId: OrgId,
    geo: z
      .object({
        latitude: z.number(),
        longitude: z.number(),
        timezone: z.string().min(1),
        elevationMeters: z.number().nonnegative().optional(),
      })
      .strict(),
    receiptDefaults: z
      .object({
        orgName: z.string().min(1),
        orgAddress: z.string().optional(),
        taxId: z.string().optional(),
        receiptFooter: z.string().optional(),
      })
      .strict()
      .optional(),
    feeRecovery: FeeRecoveryRulesetSchema,
    moderationDefault: z.enum(["auto_approve", "manual_review"]),
    updatedAt: UnixTs,
  })
  .strict();
export type TenantParnasHayomConfig = z.infer<
  typeof TenantParnasHayomConfigSchema
>;

// ---------------------------------------------------------------------------
// List Responses (Connection pattern)
// ---------------------------------------------------------------------------

export const FamilyDonationHistoryListResponseSchema = z
  .object({
    items: z.array(FamilyDonationHistoryItemSchema),
    nextToken: z.string().nullable().optional(),
    totalCount: z.number().int().nonnegative(),
  })
  .strict();
export type FamilyDonationHistoryListResponse = z.infer<
  typeof FamilyDonationHistoryListResponseSchema
>;

export const DailyHonoreesResponseSchema = z
  .object({
    items: z.array(DailyHonoreeSchema),
    date: z.string().min(10),
    hebrewDateLabel: z.string(),
  })
  .strict();
export type DailyHonoreesResponse = z.infer<typeof DailyHonoreesResponseSchema>;

export const LedgerListResponseSchema = z
  .object({
    items: z.array(LedgerEntrySchema),
    nextToken: z.string().nullable().optional(),
    totalCount: z.number().int().nonnegative(),
  })
  .strict();
export type LedgerListResponse = z.infer<typeof LedgerListResponseSchema>;

export const DonationCampaignListResponseSchema = z
  .object({
    items: z.array(DonationCampaignSchema),
    nextToken: z.string().nullable().optional(),
    totalCount: z.number().int().nonnegative(),
  })
  .strict();
export type DonationCampaignListResponse = z.infer<
  typeof DonationCampaignListResponseSchema
>;

export const PendingDedicationsListResponseSchema = z
  .object({
    items: z.array(
      DonationSchema.pick({
        id: true,
        campaignId: true,
        targetGregorianDate: true,
        hebrewYear: true,
        hebrewMonth: true,
        hebrewDay: true,
        dedicationType: true,
        honoreeName: true,
        dedicationMessage: true,
        moderationStatus: true,
        donorFirstName: true,
        donorLastName: true,
        isAnonymous: true,
        publicDisplayName: true,
        createdAt: true,
      }),
    ),
    nextToken: z.string().nullable().optional(),
    totalCount: z.number().int().nonnegative(),
  })
  .strict();
export type PendingDedicationsListResponse = z.infer<
  typeof PendingDedicationsListResponseSchema
>;
